import React, { useState, useEffect } from "react";
import {
  TextField,
  Button,
  Box,
  Typography,
  Radio,
  RadioGroup,
  FormControlLabel,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormHelperText,
  SelectChangeEvent,
  SvgIcon,
  CircularProgress,
  ToggleButtonGroup,
  ToggleButton,
  Switch,
  Checkbox,
  Divider,
  styled,
  SwitchProps,
} from "@mui/material";
import axiosInstance from "../../../../axios/axios";
import dayjs, { Dayjs } from "dayjs";
import { useEncryptService, useStorageManager, useUTCDate } from "../../../../shared/hooks";
import CredentialForm from "../CredentialForm";
import ScheduleForm from "../ScheduleForm";
import SSHService from "../../../../shared/hooks/SSHService";
import IOSSwitch from "../../../../components/IOSSwitch";
import ReusableSSHAPI from "../ReusableSSHAPI";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { END_POINTS } from "../../../../shared";
import { resetUserTypeFlags, setIsBothCloudAndPremAssetUser, setIsNewUserTrue, setIsOnlyCloudUser, setIsOnlyPremAssetUser } from "../../../../modules/authentication/login/Login_Details_slice";
import { useDispatch } from "react-redux";
// Regular expression for IP address validation
const ipRegex =
  /^(25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)\.(25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)\.(25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)\.(25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)$/;
const urlRegex = /^(https?:\/\/)?([a-zA-Z0-9\-]+\.)+[a-zA-Z]{2,6}(\/[^\s]*)?$/;
interface FrequencyOptions {
  frequency?: any;
  timeZone?: any;
  date?: any;
  timeOptions?: any;
  Days?: any;
  timeNowOptions?: any;
}
interface Props {
  onDirty: (dirty: boolean) => void; // Tracks dirty state for unsaved changes
  onSubmit?: () => void; // Optional callback on submit
  vendorList: any[]; // ✅ Accepts vendor list pro
  scheduleOptions?: FrequencyOptions;
  vendorSelected?: any; // Make selectedVendor optional
  component?: any;
}
interface Category {
  id: string;
  name: string;
}

const SSHComponent: React.FC<Props> = ({
  onDirty,
  vendorList,
  scheduleOptions,
  vendorSelected,
  component
}) => {
  const { doEncryptLogin } = useEncryptService();
  const navigate = useNavigate();
  const { getFromLocalStorage, saveToLocalStorage } = useStorageManager();
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const dispatch = useDispatch();

  // Handle form submission
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
  }

  const appendEntriesToFormData = (formData: FormData, data: Record<string, any>) => {
    Object.entries(data).forEach(([key, value]) => {
      if (value === undefined || value === null) {
        return;
      }

      if (value instanceof File) {
        formData.append(key, value, value.name);
        return;
      }

      if (value instanceof Blob) {
        formData.append(key, value);
        return;
      }

      if (typeof value === "boolean") {
        formData.append(key, value ? "true" : "false");
        return;
      }

      if (Array.isArray(value) || typeof value === "object") {
        formData.append(key, JSON.stringify(value));
        return;
      }

      formData.append(key, String(value));
    });
  };

  const handleCallbackFromSSH = async (ssh_data: any, ssh_upload: boolean = false, files: File[] = []) => {
    setIsLoading(true); // Set loading state when starting API call
    try {
      console.log("Message from ReusableSSHAPI:");
      console.log(ssh_data);
      const normalizedData: Record<string, any> = { ...ssh_data };
      delete normalizedData.component;
      const hasPrivateKeyFile =
        normalizedData.loginParameter === "private_key" &&
        normalizedData.private_key instanceof File;

      if (!hasPrivateKeyFile && normalizedData.private_key instanceof File) {
        delete normalizedData.private_key;
      }

      if (ssh_upload && files.length > 0) {
        const formData = new FormData();
        files.forEach((item) => {
          formData.append("resumes", item, item.name);
        })
        if (normalizedData) {
          appendEntriesToFormData(formData, normalizedData);
        }
        const response = await SSHService.submitSSHUpload(formData);
        console.log("SSH Data Submitted Successfully:", response);
        if (response.status = 200) {
          const res = await axiosInstance.get(END_POINTS.IS_NEW_USER);
          if (res.status === 200) {
            const isNewUser = res?.data?.data || false;
            saveToLocalStorage("isNewUserDetails", isNewUser);
            navigate("/on-premises/scans")
          }
          toast.success(response.message)
        } else {
          toast.error(response.message)
        }
        navigate("/on-premises/scans")
      } else {
        try {
          let response;
          if (hasPrivateKeyFile) {
            const formData = new FormData();
            appendEntriesToFormData(formData, normalizedData);
            response = await SSHService.submitSSHData(formData);
          } else {
            response = await SSHService.submitSSHData(normalizedData);
          }
          console.log("SSH Data Submitted Successfully:", response);
          if (response.status = 200) {
            toast.success(response.message)
          } else {
            toast.error(response.message)
          }
          await assetCount();
          navigate("/on-premises/scans")
        } catch (error: any) {
          console.error("Error submitting SSH data:", error);
          console.error("Upload Error:", error);
          const response = error.response;
          if (response && response.data && response.data.status) {
            toast.error(response.data.message)
          }
        }
      }
    } finally {
      setIsLoading(false); // Reset loading state regardless of success/failure
    }
  };

  const assetCount = async () => {
    console.log("assetCount called");
    dispatch(resetUserTypeFlags());


    const res = await axiosInstance.get(END_POINTS.IS_NEW_USER);
    let parsed = null;
    if (res.status === 200) {
      dispatch(setIsOnlyPremAssetUser({ isOnlyPremAssetUser: false }));
      dispatch(setIsOnlyCloudUser({ isOnlyCloudUser: false }));
      dispatch(setIsBothCloudAndPremAssetUser({ isBothCloudAndPremAssetUser: false }));
      const isNewUser = res?.data?.data || false;
      parsed = res?.data?.data || false;
      if (parsed.device_count == 0 && parsed.cloud_count == 0) {
        dispatch(setIsNewUserTrue({ isNewUserTrue: true }));
        //dispatch(setIsOnlyCloudUser({ is: true }));

      }
      else if (parsed.device_count > 0 && parsed.cloud_count == 0) {
        dispatch(setIsOnlyPremAssetUser({ isOnlyPremAssetUser: true }));
      } else if (parsed.device_count == 0 && parsed.cloud_count > 0) {
        dispatch(setIsOnlyCloudUser({ isOnlyCloudUser: true }));
      } else if (parsed.device_count > 0 && parsed.cloud_count > 0) {
        dispatch(setIsBothCloudAndPremAssetUser({ isBothCloudAndPremAssetUser: true }));
      }
    }
  }

  return (
    <>
      <Box sx={{ p: 2, overflowY: "auto", height: "calc(100vh - 80px)" }} className="scroll">
        <ReusableSSHAPI
          onDirty={onDirty}
          scheduleOptions={scheduleOptions}
          vendorList={vendorList}
          vendorSelected={vendorSelected}
          component={component != null ? component : "ssh"}
          onCallback={handleCallbackFromSSH}
          isLoading={isLoading}
        />
      </Box>
    </>
  );
};

export default SSHComponent;
